/**
 * Uyaran şekillerinin ekrandaki çizimi.
 *
 * Geometri burada DEĞİL: `@nirengi/engine`'in `render/shapes.ts`'inde.
 * Yazdırma kanalı da aynı tablodan okuyor, böylece tablette görünen
 * şekille A4'e basılan şekil birebir aynı. İki kopya tutmanın bedelini
 * `EGZERSIZ_ADI` ile bir kez ödedik.
 *
 * SVG, dize olarak gömülmüyor (motorun `sekilSvg`'si kâğıt içindir);
 * burada React düğümü olarak kuruluyor. Sebep `dangerouslySetInnerHTML`
 * kaçınmak değil — motorun ürettiği dize zaten kapalı bir kümeden
 * geliyor — React'in yeniden çizimde düğümü tanıyabilmesi: dize gömmek
 * her hücre değişiminde DOM'u baştan kurduruyordu.
 */

import { GOZ_KUTU, GOZ_OLCU, SEKILLER, SEKIL_ADI, gozParcalari, sekilMi } from '@nirengi/engine'
import type { GozCizimi, ShapeName } from '@nirengi/engine'

/** Karıştırıcı kümede şeklin üstüne çizilen tam çember */
const CEMBER = SEKILLER.daire.d

export function Sekil({ ad }: { ad: ShapeName }) {
  const s = SEKILLER[ad]
  return (
    <svg
      className="sek"
      viewBox="0 0 100 100"
      role="img"
      aria-label={SEKIL_ADI[ad]}
      focusable="false"
    >
      <path
        d={s.d}
        /**
         * Renk `currentColor`: saran öğe belirliyor.
         *
         * Koyu tema, seçili şık ve doğru/yanlış işareti aynı şekli
         * farklı renkte çiziyor; şeklin kendisi hiçbirini bilmiyor.
         */
        fill={s.dolu ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={s.dolu ? 2 : 9}
        transform={s.donme ? `rotate(${s.donme} 50 50)` : undefined}
      />
      {s.cember && <path d={CEMBER} fill="none" stroke="currentColor" strokeWidth={7} />}
    </svg>
  )
}

/**
 * Bir uyaran: şekilse çizilir, metinse yazılır.
 *
 * Çizim modelindeki her uyaran alanı bu ikisinden biri; hangisi olduğu
 * türde duruyor, çalışma anında aranmıyor.
 */
export function Uyaran({ shape, glyph }: { shape?: ShapeName; glyph?: string }) {
  if (shape) return <Sekil ad={shape} />
  return <>{glyph ?? ''}</>
}

/** Cevapta taşınan kimlik: şekil adı ya da metnin kendisi */
export function kimlik(i: { shape?: ShapeName; glyph?: string }): string {
  return i.shape ?? i.glyph ?? ''
}

/**
 * Yalnızca KİMLİK dizesi elimizdeyken çizim.
 *
 * Damgalama tahtası, palet seçimi ve cevap kutusu kimlik dizesi
 * taşıyor (çizim modelindeki tür bilgisi oraya kadar gelmiyor, çünkü
 * oradaki şey artık çocuğun verdiği cevap). Dize bir şekil adıysa
 * şekil çiziliyor, değilse metin yazılıyor.
 */
export function UyaranAdi({ ad }: { ad: string }) {
  /**
   * Metin parçası kendi kutusunda.
   *
   * Dizi aralığı 8 piksel ve bu, tek karakterlik parçalar (rakam,
   * şekil) için yeterliydi. Rota cevabı dokuz durak adı olunca
   * "KonakPınarOrmanKale" diye okunuyordu: çocuk kendi yazdığını
   * okuyamıyor, yanlış durağı sildiğini fark edemiyor.
   */
  return sekilMi(ad) ? <Sekil ad={ad} /> : <span className="parca">{ad}</span>
}

/**
 * Boşlukla ayrılmış kimlik dizisi — dizi hatırlamanın cevabı böyle
 * tutuluyor ("daire kare ucgen"). Her parça ayrı çiziliyor.
 */
export function Dizi({ metin, bos = '—' }: { metin: string; bos?: string }) {
  const parcalar = metin.split(' ').filter(Boolean)
  if (parcalar.length === 0) return <>{bos}</>
  return (
    <span className="dizi">
      {parcalar.map((p, i) => (
        <UyaranAdi key={i} ad={p} />
      ))}
    </span>
  )
}

/**
 * Şematik göz çevresi çizimi.
 *
 * Geometri burada DEĞİL: motorun `render/gozler.ts`'i veriyor ve
 * yazdırma kanalı aynı fonksiyonu çağırıyor. Tablette görünen gözle
 * A4'e basılan göz birebir aynı — bu projede aynı hata iki kez oldu
 * (örtüşen şekil kutusunun ölçüsü, kesikli çizgi deseni) ve ikisinde de
 * sebep ekranın kâğıdın okuduğu sayıyı okumamasıydı.
 *
 * `clipPath` kimliği `onek` ile tekil: aynı kimlik sayfada iki kez
 * geçerse ikinci göz birincinin kapağıyla kırpılıyor.
 */
export function Gozler({ cizim, onek }: { cizim: GozCizimi; onek: string }) {
  const parcalar = gozParcalari(cizim)
  return (
    <svg className="gozcizim" viewBox={GOZ_KUTU} role="img" aria-label="göz çizimi" focusable="false">
      {parcalar.map((p, i) => {
        const kid = `${onek}${i}`
        return (
          <g key={i}>
            <defs>
              <clipPath id={kid}>
                <path d={p.kapak} />
              </clipPath>
            </defs>
            <path
              d={p.kapak}
              fill="none"
              stroke="currentColor"
              strokeWidth={GOZ_OLCU.kapakCizgi}
              strokeLinejoin="round"
            />
            <g clipPath={`url(#${kid})`}>
              <circle
                cx={p.ix}
                cy={p.iy}
                r={GOZ_OLCU.iris}
                fill="none"
                stroke="currentColor"
                strokeWidth={GOZ_OLCU.irisCizgi}
              />
              <circle cx={p.ix} cy={p.iy} r={GOZ_OLCU.bebek} fill="currentColor" />
            </g>
            <path
              d={p.kas}
              fill="none"
              stroke="currentColor"
              strokeWidth={GOZ_OLCU.kasCizgi}
              strokeLinecap="round"
            />
          </g>
        )
      })}
    </svg>
  )
}
